import random from 'random'

// The RNG every generator draws from. `withSeed` swaps this for a seeded
// instance for the duration of a synchronous call and then restores it.
let current: { int: (min: number, max: number) => number } = random;

/**
 * Runs `fn` with randomness drawn from `seed`, then restores the previous RNG.
 *
 * Only sound because generation is entirely synchronous: nothing may await or
 * yield between the swap and the restore. A generator that becomes async breaks
 * seeding silently. See ADR 0009 §2.
 */
export function withSeed<T>(seed: string, fn: () => T): T {
    const previous = current;
    current = random.clone(seed);
    try {
        return fn();
    } finally {
        current = previous;
    }
}

export function getRandomInt(min, max) {
    const result = current.int(min, max);
    return result;
}

/**
 * Fisher-Yates, through `getRandomInt` rather than `Math.random`, so a
 * shuffle stays reproducible under `withSeed` (ADR 0009).
 *
 * This used to be written three times over - `AveragesGenerator`,
 * `BarGraphGenerator` and `ProblemGenerator.tsx`'s `shuffleArray` - plus a
 * fourth, differently-named copy in `FlashCards/deck.ts`. ADR 0033 §1's rule
 * is one copy per algorithm; this is that copy. Returns a new array; callers
 * that relied on the old in-place `shuffleArray` now reassign instead.
 */
export function shuffle<T>(list: T[]): T[] {
    const shuffled = [...list];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = getRandomInt(0, i);
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
}

/**
 * `count` entries drawn from `types`, for a Mixed sheet (ADR 0041 §1).
 *
 * Every type appears `floor(count / types.length)` or `ceil(...)` times -
 * that "even coverage" is the property the old
 * `types[problems.length % types.length]` rotation got right, and is worth
 * keeping. What that rotation got wrong is *which* types received the extra
 * problem: always the ones earliest in the enum, on every sheet, forever,
 * because the formula never consulted the RNG. Here, which types get the
 * extra is chosen by shuffling `types` and taking the first `remainder` of
 * them - and the whole sequence is shuffled again before it is returned, so
 * a page does not read as a fixed cycle a student can predict from position
 * alone.
 *
 * Not weighted: a Mixed sheet's job is to be even, not to favour the types a
 * teacher might consider "harder" - see ADR 0041's alternatives.
 */
export function questionSequence<T>(types: T[], count: number): T[] {
    if (count <= 0 || types.length === 0) {
        return [];
    }
    const base = Math.floor(count / types.length);
    const remainder = count - base * types.length;
    const extra = new Set(shuffle(types).slice(0, remainder));

    const sequence: T[] = [];
    for (const type of types) {
        const times = extra.has(type) ? base + 1 : base;
        for (let i = 0; i < times; i++) {
            sequence.push(type);
        }
    }
    return shuffle(sequence);
}

export const SEED_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

/** Six upper-case base-36 characters — the worksheet ID printed in the footer. */
export function newSeed(): string {
    let seed = '';
    for (let i = 0; i < 6; i++) {
        seed += SEED_ALPHABET[getRandomInt(0, SEED_ALPHABET.length - 1)];
    }
    return seed;
}
